import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingBag, User, Menu, X, LogOut, Heart } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import SearchOverlay from './SearchOverlay';
import { useWishlist } from '../context/WishlistContext';

const nav = [
  { label: 'Bags', to: '/shop/bags' },
  { label: 'The Desk', to: '/shop/desk' },
  { label: 'Small Leather Goods', to: '/shop/small-leather' },
  { label: 'Gifting', to: '/shop/gifting' },
  { label: 'Our Story', to: '/our-story' }
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { count: wishCount } = useWishlist();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setUserMenu(false); }, [location.pathname]);

  // Don't render on admin routes for cleaner UI
  if (location.pathname.startsWith('/admin')) return null;
  if (location.pathname === '/auth-callback') return null;

  return (
    <header className={`sticky top-0 z-50 transition-colors duration-500 ${scrolled ? 'bg-[#F4EEE4]/95 backdrop-blur border-b border-black/10' : 'bg-[#F4EEE4]'}`}>
      <div className="bg-[#1a1a1a] text-[#F4EEE4] text-[11px] tracking-widest-plus uppercase">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between">
          <span className="hidden md:inline">Complimentary shipping on orders over &#8377;5,000</span>
          <span className="md:hidden">Free shipping over &#8377;5,000</span>
          <span className="hidden md:inline">Handcrafted in India</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <button className="lg:hidden" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <Menu className="w-5 h-5" />
          </button>
          <button data-testid="search-open-btn" aria-label="Search" onClick={() => setSearchOpen(true)} className="flex items-center gap-2 text-xs tracking-widest-plus uppercase">
            <Search className="w-4 h-4" /> <span className="hidden lg:inline">Search</span>
          </button>
        </div>

        <Link to="/" className="font-serif-display text-2xl md:text-3xl tracking-tight">
          barebasics<span className="text-[#C6410E]">.</span>
        </Link>

        <div className="flex items-center gap-5 flex-1 justify-end relative">
          <div className="relative">
            <button onClick={() => user ? setUserMenu(v => !v) : navigate('/login')} aria-label="Account" className="hidden md:block">
              <User className="w-5 h-5" />
            </button>
            {user && userMenu && (
              <div className="absolute right-0 top-8 bg-[#F4EEE4] border border-black/10 py-3 min-w-[200px] shadow-lg">
                <p className="px-4 pb-2 text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/60">{user.email}</p>
                <Link to="/account" className="block px-4 py-2 text-sm hover:bg-[#E9E1D4]">Your account</Link>
                <button onClick={async () => { await logout(); navigate('/'); }} className="w-full text-left px-4 py-2 text-sm hover:bg-[#E9E1D4] flex items-center gap-2"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
              </div>
            )}
          </div>
          <button data-testid="nav-wishlist-btn" aria-label="Wishlist" className="relative" onClick={() => navigate(user ? '/account#wishlist' : '/login')}>
            <Heart className="w-5 h-5" />
            {wishCount > 0 && (
              <span data-testid="nav-wishlist-count" className="absolute -top-2 -right-2 bg-[#1a1a1a] text-[#F4EEE4] text-[10px] min-w-4 h-4 px-1 rounded-full flex items-center justify-center">{wishCount}</span>
            )}
          </button>
          <button aria-label="Cart" className="relative" onClick={() => navigate('/checkout')}>
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#C6410E] text-white text-[10px] min-w-4 h-4 px-1 rounded-full flex items-center justify-center">{count}</span>
            )}
          </button>
        </div>
      </div>

      <nav className="hidden lg:block border-t border-black/10">
        <ul className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-10 py-4">
          {nav.map((n) => (
            <li key={n.to}>
              <NavLink to={n.to} className={({ isActive }) => `text-[11px] tracking-widest-plus uppercase link-underline ${isActive ? 'text-[#C6410E]' : 'text-[#1a1a1a]'}`}>
                {n.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {menuOpen && (
        <div className="fixed inset-0 z-[60] bg-[#F4EEE4] lg:hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-black/10">
            <span className="font-serif-display text-2xl">barebasics<span className="text-[#C6410E]">.</span></span>
            <button onClick={() => setMenuOpen(false)} aria-label="Close menu"><X className="w-5 h-5" /></button>
          </div>
          <ul className="px-6 py-6 space-y-5">
            {nav.map((n) => (
              <li key={n.to}>
                <NavLink to={n.to} onClick={() => setMenuOpen(false)} className={({ isActive }) => `font-serif-display text-3xl ${isActive ? 'text-[#C6410E]' : ''}`}>
                  {n.label}
                </NavLink>
              </li>
            ))}
            <li><NavLink to="/contact" onClick={() => setMenuOpen(false)} className="font-serif-display text-3xl">Contact</NavLink></li>
            <li>
              {user
                ? <NavLink to="/account" onClick={() => setMenuOpen(false)} className="font-serif-display text-3xl">Your account</NavLink>
                : <NavLink to="/login" onClick={() => setMenuOpen(false)} className="font-serif-display text-3xl">Sign in</NavLink>}
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
